/**
 * PriceChart — 이력 시세 라인 차트 (PKA 화면정의서 SC-007 "이력 가격 책정" 그래프).
 *
 * 차트 라이브러리를 새로 넣지 않고 인라인 SVG 로 그린다. 선 하나 + 면 채우기 +
 * 실거래 점이 전부라서 의존성을 늘릴 이유가 없다.
 *
 * **선은 추정, 점은 실제다.** 추세선만 그리면 "6건이 53,700~72,700 에 팔렸다"는
 * 옆의 표기와 그래프가 서로를 반박한다 — 선은 그 값을 지나가지 않기 때문이다.
 * 실제 체결을 점으로 얹고 세로 범위를 점까지 포함해 잡으면 그 모순이 사라지고,
 * 산포가 넓다는 사실 자체가 신뢰도 배지의 근거로 눈에 보인다 (StockX 방식).
 *
 * ── 픽셀 + 격자 (2026-08-25) ─────────────────────────────────────────
 * 이 앱의 아이콘·CTA 모서리·스캔 프레임이 전부 픽셀 격자 위에 있는데 차트만
 * 매끈한 곡선이라 혼자 다른 화면에서 온 것처럼 보였다. 세 가지를 바꿨다:
 *   · 선을 **계단**으로 — 한 칸 가로로 간 다음 세로로 꺾는다 (step-after).
 *     값을 왜곡하지 않는다: 꺾이는 지점이 곧 그 날의 값이다.
 *   · 값을 **칸에 맞춰 스냅** — 세로도 칸 단위로 떨어뜨려 반픽셀 얼룩을 없앤다.
 *   · 뒤에 **격자**를 깐다 — 눈금이 아니라 바탕이다(축 라벨은 이 컴포넌트 밖).
 * shape-rendering="crispEdges" 로 확대·축소해도 선이 흐려지지 않는다.
 *
 * ⚠ 스냅은 **보이는 것만** 바꾼다. 세로 범위(axisMin/Max)와 실거래 값은 그대로다 —
 *   칸 하나가 세로 범위의 1/14 이라 반올림 오차가 값의 뜻을 바꾸지 않는다.
 *
 * 색은 토큰만 쓴다 — 상승 `--price-up` / 하락 `--price-down`.
 * 색만으로 방향을 읽게 하지 않도록, 이 컴포넌트를 쓰는 화면이 반드시 GraphUp/GraphDown 과
 * 변동률 텍스트를 함께 보여준다 (CardDetailPage 의 Trend).
 */
import { useId } from "react";
import { priceDeltaColor } from "../card/PriceDelta";
import styles from "./PriceChart.module.css";

export interface ChartTrade {
  /** 오늘로부터 며칠 전 (0 = 오늘) */
  daysAgo: number;
  prcAmt: number;
}

interface PriceChartProps {
  /** 오래된 → 최근 순의 일별 가격. 2개 미만이면 아무것도 그리지 않는다. */
  points: number[];
  /** 기간 상승 여부 — 선·채우기 색을 고른다. */
  isUp: boolean;
  /** 스크린리더용 요약 (예: "30일 시세 추이, 4.2% 상승"). */
  ariaLabel: string;
  /** 선 위에 얹을 실제 체결 건. 없으면 선만 그린다. */
  trades?: ChartTrade[];
  /** 이 그래프가 덮는 일수 — 실거래 점의 가로 위치를 정한다. */
  days?: number;
  /** 선과 점을 모두 담는 세로 범위. 없으면 선만 기준으로 잡는다. */
  axisMin?: number;
  axisMax?: number;
}

/** viewBox 좌표계. 실제 크기는 CSS 가 정하고 vector-effect 로 선 굵기를 고정한다. */
const VB_W = 300;
const VB_H = 96;
const PAD_Y = 6;
/** 픽셀 한 칸. 300×96 을 나누어떨어지게 잡는다 (가로 30칸 · 세로 16칸). */
const CELL_X = 10;
const CELL_Y = 6;
/** 눈에 보이는 격자는 두 칸마다 — 한 칸마다 그으면 선이 배경을 덮는다. */
const GRID_STEP = 2;
/**
 * 실거래 표식 한 변 — **정사각**이다 (2026-08-25).
 * 칸(10×6)을 그대로 쓰면 가로로 눕는데, 픽셀은 정사각이라야 픽셀로 읽힌다.
 * 세로 칸(6)에 맞춘다 — 한 줄 높이에 딱 들어가고, 전에 쓰던 원(지름 7)과도
 * 무게가 비슷해 그래프가 표식에 먹히지 않는다.
 * ⚠ SVG 는 preserveAspectRatio 기본값(uniform)이라 6×6 이 화면에서도 정사각이다.
 */
const DOT = CELL_Y;
const snap = (v: number, cell: number) => Math.round(v / cell) * cell;

export default function PriceChart({
  points,
  isUp,
  ariaLabel,
  trades = [],
  days,
  axisMin,
  axisMax,
}: PriceChartProps) {
  const gradientId = useId();

  if (points.length < 2) return null;

  // 세로 범위는 호출자가 준 값을 우선한다 (실거래 점까지 포함한 범위).
  const min = axisMin ?? Math.min(...points);
  const max = axisMax ?? Math.max(...points);
  const span = max - min || 1;

  const toY = (value: number) => PAD_Y + (1 - (value - min) / span) * (VB_H - PAD_Y * 2);

  // 칸에 맞춰 떨어뜨린다 — 반픽셀에 걸친 선은 crispEdges 에서 굵기가 들쭉날쭉해진다.
  const coords = points.map((value, i) => ({
    x: snap((i / (points.length - 1)) * VB_W, CELL_X),
    y: snap(toY(value), CELL_Y),
  }));

  /**
   * 계단 선 — 가로로 한 칸 간 뒤 세로로 꺾는다(step-after).
   * 대각선을 없애면 격자 위의 픽셀 그림이 되고, crispEdges 와도 맞는다.
   */
  const line = coords
    .map((p, i) => (i === 0 ? `M${p.x} ${p.y}` : `H${p.x} V${p.y}`))
    .join(" ");
  const area = `${line} V${VB_H} H0 Z`;
  const last = coords[coords.length - 1];
  const stroke = priceDeltaColor(isUp ? 1 : -1);

  // daysAgo=days → 왼쪽 끝, daysAgo=0 → 오른쪽 끝.
  const spanDays = days ?? points.length - 1;
  const tradeDots = trades.map((tr) => ({
    x: snap(spanDays > 0 ? ((spanDays - tr.daysAgo) / spanDays) * VB_W : VB_W, CELL_X),
    y: snap(toY(tr.prcAmt), CELL_Y),
  }));

  return (
    <svg
      className={styles.chart}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label={ariaLabel}
      shapeRendering="crispEdges"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* 격자 — 값이 아니라 바탕이다. 읽으라고 두는 눈금이 아니므로 색을 낮춘다.
          ⚠ aria 로는 아무 말도 하지 않는다(role="img" 의 label 이 이미 요약한다). */}
      <g className={styles.grid} aria-hidden="true">
        {Array.from({ length: Math.floor(VB_W / (CELL_X * GRID_STEP)) + 1 }, (_, i) => (
          <line key={`v${i}`} x1={i * CELL_X * GRID_STEP} y1={0} x2={i * CELL_X * GRID_STEP} y2={VB_H} />
        ))}
        {Array.from({ length: Math.floor(VB_H / (CELL_Y * GRID_STEP)) + 1 }, (_, i) => (
          <line key={`h${i}`} x1={0} y1={i * CELL_Y * GRID_STEP} x2={VB_W} y2={i * CELL_Y * GRID_STEP} />
        ))}
      </g>
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        /* 계단의 모서리는 각져야 한다 — round 로 두면 꺾이는 자리가 뭉개진다 */
        strokeLinecap="butt"
        strokeLinejoin="miter"
        vectorEffect="non-scaling-stroke"
      />
      {/* 실제 체결 — 속을 배경색으로 비워 추세선과 구분한다. 점이 선을 벗어난
          만큼이 곧 "이 시세가 얼마나 흔들리는가" 다. */}
      {tradeDots.map((d, i) => (
        <rect
          key={i}
          x={d.x - DOT / 2}
          y={d.y - DOT / 2}
          width={DOT}
          height={DOT}
          fill="var(--color-background-card, var(--color-background-body))"
          stroke={stroke}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {/* 현재 시점 — 유일하게 속이 찬 칸 */}
      <rect
        x={last.x - DOT / 2}
        y={last.y - DOT / 2}
        width={DOT}
        height={DOT}
        fill={stroke}
      />
    </svg>
  );
}
